import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify';
import moment from 'moment';
import DocumentGallery from './DocumentGallery';
import FullPageLoader from '@common/FullPageLoader';
import Modal from '@src/common/ModalPopup';
import ReactSelect from '@common/ReactSelect';
import { getPolicyTermsFinalized, confirmPolicyTermsNoChange, savePolicyTermsFinalized, getPolicyTermsAgencyRecalcPreview, updateLeadDetailsInStore } from '@src/store/slices/insurance';
import { ROLE_FEATURES, STATUS_SUB_STATUS, STATUS_SUCCESS, WITH_RULE_ENGINE_ID, WITHOUT_RULE_ENGINE, DOCUMENT_CATEGORY, LEAD_TYPE_CRM } from "@src/config/constant";
import * as HELPER from "../../../config/helper";
import { useLanguage } from '@src/context/LanguageContext';

const TENURE_OPTIONS = [1, 2, 3, 4, 5];
// Mirrors backend global.SPECIAL_ADDON_START_ID.
const SPECIAL_ADDON_START_ID = 8;
// Mirrors backend global.POLICY_HOLDER / PERSONAL_OFFSET_POINTS_TAX_PERCENT / COMPANY_OFFSET_POINTS_TAX_PERCENT.
const POLICY_HOLDER_PERSONAL = 1;
const POLICY_HOLDER_COMPANY = 2;
const PERSONAL_OFFSET_POINTS_TAX_PERCENT = 2.5;
const COMPANY_OFFSET_POINTS_TAX_PERCENT = 2;

// Reverse of HELPER.addCurrencyFormatting — called only on raw, unformatted keystrokes (see
// AmountInput below), so either '.' or ',' typed by the user is unambiguously the decimal point;
// the LAST one wins and everything before it is grouping noise to strip. These amounts can load
// from the backend with a fractional Rupiah (tax-adjusted cashback, percentage-derived premiums),
// so the fraction has to survive editing instead of being merged into the integer.
const parseAmountInput = (raw: string): string => {
    if (!raw) return '';
    const lastSep = Math.max(raw.lastIndexOf(','), raw.lastIndexOf('.'));
    if (lastSep === -1) return raw.replace(/[^\d-]/g, '');
    const intPart = raw.slice(0, lastSep).replace(/[^\d-]/g, '');
    const fracPart = raw.slice(lastSep + 1).replace(/[^\d]/g, '');
    return fracPart ? `${intPart}.${fracPart}` : intPart;
};

// A currency input that reformats on every keystroke (plain addCurrencyFormatting on `value`) can
// never let the user type a decimal separator — the instant they press it with no fraction digits
// yet, toLocaleString drops the bare trailing separator and it looks like the keystroke did nothing.
// This shows the user's literal keystrokes while focused and only renders the pretty "Rp 1.234.567"
// form once they leave the field, while still pushing the parsed numeric string up on every change
// so live totals (GWP, final premium, …) keep updating as they type.
const AmountInput = (props: { value: string; onValueChange: (v: string) => void; disabled?: boolean; title?: string }) => {
    const [focused, setFocused] = useState(false);
    const [raw, setRaw] = useState('');
    return (
        <input
            type="text"
            className="form-input"
            placeholder=" "
            autoComplete="off"
            disabled={props.disabled}
            title={props.title}
            value={focused ? raw : HELPER.addCurrencyFormatting(props.value, true)}
            // Without select-all, clicking in and typing over a "0" default appends instead of replacing
            // it (e.g. typing "545444" into a "0" field produces "0545444"). select() has to wait a tick —
            // called synchronously here it selects the still-formatted display text, but the setRaw/
            // setFocused update immediately after swaps the DOM value to the raw string, which collapses
            // whatever was selected back to a bare cursor.
            onFocus={(e: any) => {
                setRaw(props.value || '');
                setFocused(true);
                const input = e.target;
                setTimeout(() => input.select(), 0);
            }}
            onBlur={() => setFocused(false)}
            onChange={(e: any) => {
                setRaw(e.target.value);
                props.onValueChange(parseAmountInput(e.target.value));
            }}
        />
    );
};

// Same raw-while-focused problem as AmountInput above, but for the two editable percent fields
// (Discount %, Cashback %): their displayed value is recomputed from the live amount on every
// render, so without buffering, typing "1." immediately snaps back to "1" (Number("1.") === 1)
// and the decimal point can never be typed.
const PercentInput = (props: { value: string | number; onValueChange: (v: string) => void; disabled?: boolean }) => {
    const [focused, setFocused] = useState(false);
    const [raw, setRaw] = useState('');
    const displayValue = (props.value === '' || props.value === null || props.value === undefined) ? '' : `${props.value}%`;
    return (
        <input
            type="text"
            className="form-input"
            placeholder=" "
            autoComplete="off"
            disabled={props.disabled}
            value={focused ? raw : displayValue}
            onFocus={(e: any) => {
                setRaw((props.value === '' || props.value === null || props.value === undefined) ? '' : String(props.value));
                setFocused(true);
                const input = e.target;
                setTimeout(() => input.select(), 0);
            }}
            onBlur={() => setFocused(false)}
            onChange={(e: any) => {
                const next = e.target.value.replace(/[^0-9.]/g, '');
                setRaw(next);
                props.onValueChange(next);
            }}
        />
    );
};

const emptyYear = (insurance_type_id?: number, masterAddons: any[] = []) => ({
    estimated_tsi: '',
    accessories_price: '',
    basic_premium_amount: '',
    addons: unselectedAddonsFromMaster(masterAddons),
    insurance_type_id,
});

// Year 1 offers the full addon catalog — every addon the insurer supports for this vehicle/insurance
// type (current.addon_catalog, from the Rule Engine's insurer-addon mapping). Every other year mirrors
// only whichever addons are currently SELECTED on Year 1 — not the full catalog, and not that year's
// own saved rows — so a 2nd/3rd year's options always match what Year 1 actually has picked.
const unselectedAddonsFromMaster = (masterAddons: any[]) =>
    masterAddons.map((addon: any) => ({ ...addon, selected: false, premium_amount: '' }));

const mergeYearAddonsWithMaster = (masterAddons: any[], ownAddons: any[]) => {
    const ownByAddonId = new Map((ownAddons || []).map((addon: any) => [addon.addon_id, addon]));
    return masterAddons.map((master: any) => {
        const own = ownByAddonId.get(master.addon_id);
        // Backend-sourced addons (initial load) never carry a `selected` field at all — every row
        // that exists there IS selected. A prior client-side merge round, on the other hand, always
        // sets `selected` explicitly (true or false), including unselected placeholders kept around
        // so the list stays the right length across re-merges. So: selected unless explicitly false.
        const isSelected = !!own && own.selected !== false;
        return isSelected ? { ...master, ...own, selected: true } : { ...master, selected: false, premium_amount: '' };
    });
};

const toYearsFormState = (years: any[], tenure: number, addonCatalog: any[] = []) => {
    const fullCatalog = addonCatalog?.length ? addonCatalog : (years?.[0]?.addons || []);
    const year1Source = years?.[0];
    const year1Addons = year1Source
        ? mergeYearAddonsWithMaster(fullCatalog, year1Source.addons || [])
        : unselectedAddonsFromMaster(fullCatalog);
    const year1SelectedAddons = year1Addons.filter((addon: any) => addon.selected);

    let padded = Array.from({ length: tenure }, (_, index) => {
        if (index === 0) {
            return year1Source
                ? {
                    estimated_tsi: year1Source.estimated_tsi || '',
                    accessories_price: year1Source.accessories_price || '',
                    basic_premium_amount: year1Source.basic_premium_amount || '',
                    overall_car_price: year1Source.overall_car_price || '',
                    insurance_type_id: year1Source.insurance_type_id,
                    addons: year1Addons,
                }
                : emptyYear(undefined, fullCatalog);
        }
        let source = years?.[index];
        return source
            ? {
                estimated_tsi: source.estimated_tsi || '',
                accessories_price: source.accessories_price || '',
                basic_premium_amount: source.basic_premium_amount || '',
                overall_car_price: source.overall_car_price || '',
                insurance_type_id: source.insurance_type_id,
                addons: mergeYearAddonsWithMaster(year1SelectedAddons, source.addons || []),
            }
            : emptyYear(year1Source?.insurance_type_id, year1SelectedAddons);
    });
    return padded;
};

const PolicyTermsFinalizedForm = (props: any) => {

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [locked, setLocked] = useState<boolean>(false);
    const [editing, setEditing] = useState<boolean>(false);
    const [current, setCurrent] = useState<any>(null);
    const [maxDiscountPer, setMaxDiscountPer] = useState<number>(25);
    const [cashbackUnlockPer, setCashbackUnlockPer] = useState<number>(25);

    const [plateNumber, setPlateNumber] = useState<string>('');
    const [discountAmount, setDiscountAmount] = useState<string>('');
    const [cashbackAmount, setCashbackAmount] = useState<string>('');
    const [adminFee, setAdminFee] = useState<string>('');
    const [commissionValue, setCommissionValue] = useState<string>('0');
    const [pointsValue, setPointsValue] = useState<string>('0');
    // Agency only — "Maximum Usable Points" ceiling from the live recalc preview, same field
    // AdditionalDiscountPopup.tsx (Agency App/Web) validates Cashback against.
    const [maxAgentPointsPer, setMaxAgentPointsPer] = useState<number | null>(null);
    const [ruleEngineId, setRuleEngineId] = useState<number>(WITH_RULE_ENGINE_ID);
    const [tenure, setTenure] = useState<number>(1);
    const [years, setYears] = useState<any[]>([]);
    const [activeYearTab, setActiveYearTab] = useState<number>(0);
    const [addonModalYearIndex, setAddonModalYearIndex] = useState<number | null>(null);
    const [showNonBinderConfirm, setShowNonBinderConfirm] = useState<boolean>(false);
    const [saveChangeSummary, setSaveChangeSummary] = useState<{ label: string; oldValue: string; newValue: string }[]>([]);

    const { translations } = useLanguage();
    const dispatch: any = useDispatch();
    const navigate = useNavigate();
    const queryParams = useParams();
    const insuranceState = useSelector((state: any) => state.insurance);

    const canEdit = HELPER.isUserHasUpdateAccess({ permissionID: ROLE_FEATURES['policy_terms_finalized'], accessType: 'edit', leadDetails: insuranceState.leadDetail, pageSlug: 'policy-terms-finalized' });
    // Effective Cashback is an Agency App/Web-only concept — CRM sends its own final values, this is never applicable there.
    // (isCrmLead itself is declared below, alongside isNonBinder, since it's also used by the live-recalc effect.)

    // Center panel reuses the Disbursal document tab from Policy Generated — this step can't be
    // actioned until that same document is approved, same gate ShareDetailsForm applies before Policy Shared.
    const disbursalDocs = (insuranceState.leadDetail?.images || []).filter((img: any) => +img.doc_type_id === DOCUMENT_CATEGORY['disbursal']['doc_type_id']);
    const requiredDisbursalDocs = disbursalDocs.filter((img: any) => +img.doc_id === DOCUMENT_CATEGORY['disbursal']['childs']['policy_shared']);
    const approvedDisbursalDocs = disbursalDocs.filter((img: any) => img.doc_id && +img.is_approved === 1);
    const disbursalApproved = !!requiredDisbursalDocs.length && approvedDisbursalDocs.length === disbursalDocs.length;

    const loadData = () => {
        if (!queryParams.id) return;
        setLoading(true);
        getPolicyTermsFinalized({ lead_id: queryParams.id }).then((res: any) => {
            setLoading(false);
            if (res?.status === STATUS_SUCCESS && res?.data) {
                setLocked(!!res.data.locked);
                setMaxDiscountPer(res.data.max_discount_per || 25);
                setCashbackUnlockPer(res.data.cashback_unlock_per || 25);
                setCurrent(res.data.current || null);
                resetFormFromCurrent(res.data.current);
            }
        }).catch(() => setLoading(false));
    };

    const resetFormFromCurrent = (data: any) => {
        if (!data) return;
        setPlateNumber(data.plate_number || '');
        setDiscountAmount(data.customer_discount_amount ? String(data.customer_discount_amount) : '');
        setCashbackAmount(data.customer_cashback_amount ? String(data.customer_cashback_amount) : '');
        setAdminFee(data.admin_fee ? String(data.admin_fee) : '');
        setCommissionValue(String(data.agent_commission_value || 0));
        setPointsValue(String(Math.max(0, data.agent_points_value || 0)));
        setRuleEngineId(data.rule_engine_id || WITH_RULE_ENGINE_ID);
        setTenure(data.tenure || 1);
        setYears(toYearsFormState(data.years || [], data.tenure || 1, data.addon_catalog || []));
        setActiveYearTab(0);
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryParams.id]);

    // This step's Tab.Pane never unmounts when ops switches to Quote Details/Docs Upload and back
    // (no unmountOnExit on the Tab.Container) — so a change made elsewhere, e.g. via the standalone
    // Additional Discount feature, never reaches this already-mounted snapshot without an explicit
    // refetch on return. Skipped while mid-edit so it can't clobber an unsaved in-progress edit.
    useEffect(() => {
        if (props.activeTab === 'first' && !editing) loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.activeTab]);

    const isNonBinder = +ruleEngineId === WITHOUT_RULE_ENGINE;
    const isCrmLead = +insuranceState.leadDetail?.lead_type === LEAD_TYPE_CRM;

    // Agency leads: Points are the Rule Engine's, not ops's to type in — re-derive them live as
    // Cashback changes, same formula Agency App/Web uses. Commission is handled separately below —
    // it's always the other half of the 25% cap against Discount, not something the rule engine
    // needs to be consulted for. CRM leads enter all four fields by hand (see the form below).
    // The engine prices Points off the saved GWP, so send it amounts on that basis and scale its answer back to the edited GWP.
    const editedGwp = isNonBinder
        ? years.reduce((sum, year) => sum + (Number(year.basic_premium_amount) || 0) + (year.addons || []).filter((a: any) => a.selected).reduce((acc: number, a: any) => acc + (Number(a.premium_amount) || 0), 0), 0)
        : 0;
    const previewBasisRatio = editedGwp > 0 && current?.gwp_amount > 0 ? current.gwp_amount / editedGwp : 1;
    useEffect(() => {
        if (!editing || isCrmLead || !queryParams.id) return;
        const timer = setTimeout(() => {
            getPolicyTermsAgencyRecalcPreview({
                lead_id: queryParams.id,
                customer_discount_amount: Math.round((Number(discountAmount) || 0) * previewBasisRatio),
                customer_cashback_amount: Math.round((Number(cashbackAmount) || 0) * previewBasisRatio),
            }).then((res: any) => {
                if (res?.data) {
                    // A Cashback beyond the ceiling drives the engine's own points negative — clamp the
                    // display (validate() below is what actually blocks Save on this).
                    setPointsValue(String(Math.max(0, Math.round((res.data.agent_points_value || 0) / previewBasisRatio))));
                    setMaxAgentPointsPer(res.data.max_agent_points_per ?? null);
                }
            }).catch(() => {});
        }, 500);
        return () => clearTimeout(timer);
        // discountAmount stays a dep even though only Points is read from the response — the engine's
        // Points math is computed off the same quotation as Discount, so a stale Discount would skew it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing, isCrmLead, discountAmount, cashbackAmount]);

    // ---- live derived values (client-side preview; server is authoritative on Save) ----
    const yearTotals = years.map((year) => {
        const addonPremium = (year.addons || []).filter((a: any) => a.selected).reduce((sum: number, a: any) => sum + (Number(a.premium_amount) || 0), 0);
        // A legacy Binder year with no real TSI/Accessories split (both blank - toYearsFormState
        // turns the server's null into '') has nothing to sum - fall back to the server's own
        // overall_car_price (same total_fmv-derived figure the snapshot already carries) instead
        // of silently showing Rp 0.
        const hasSplitValues = !!(Number(year.estimated_tsi) || Number(year.accessories_price));
        const overallCarPrice = hasSplitValues
            ? (Number(year.estimated_tsi) || 0) + (Number(year.accessories_price) || 0)
            : (Number(year.overall_car_price) || 0);
        return { addonPremium, overallCarPrice };
    });
    const gwpAmount = isNonBinder
        ? years.reduce((sum, year, index) => sum + (Number(year.basic_premium_amount) || 0) + yearTotals[index].addonPremium, 0)
        : (current?.gwp_amount || 0);
    const discountPer = gwpAmount ? +(((Number(discountAmount) || 0) / gwpAmount) * 100).toFixed(4) : 0;
    // Trust CRM's stored % only when GWP can't be derived live (Non-Binder with no premium
    // entered yet) — otherwise prefer the live amount/GWP calc. The stored % can come from a
    // different GWP basis than this render's (e.g. Non-Binder's per-year sum vs. whatever GWP
    // was in effect when it was last saved), so trusting it unconditionally shows a stale/wrong
    // percent (0%) next to a real, unchanged, non-zero Amount.
    const discountAmountUnchanged = Number(discountAmount || 0) === Number(current?.customer_discount_amount || 0);
    const displayDiscountPer = (discountAmountUnchanged && !gwpAmount) ? (current?.customer_discount_per || 0) : discountPer;
    const cashbackPer = gwpAmount ? +(((Number(cashbackAmount) || 0) / gwpAmount) * 100).toFixed(4) : 0;
    // CRM types the Amount in directly; Agency gets it from the live recalc preview below — either way
    // this is the value about to be persisted, not the static value the step started with. % is derived
    // for display only, same relationship as Discount/Cashback Amount vs %.
    const agentCommissionValue = Number(commissionValue) || 0;
    const agentPointsValue = Number(pointsValue) || 0;
    const agentCommissionPer = gwpAmount ? +((agentCommissionValue / gwpAmount) * 100).toFixed(4) : 0;
    const agentPointsPer = gwpAmount ? +((agentPointsValue / gwpAmount) * 100).toFixed(4) : 0;

    // Discount/Cashback/Commission/Points are stored as amounts against the GWP they were set on.
    // When ops changes Basic Premium or an Add-on amount, the GWP moves — rescale all four so each keeps
    // the same percentage of the new GWP, instead of leaving stale amounts (which read as 54% discount)
    // or wiping them. Only while Premium Determination is editable (Non-Binder); skipped on the
    // Binder -> Non-Binder switch, where the GWP basis changes without any premium having been edited.
    const gwpBasisRef = useRef<{ gwp: number; live: boolean }>({ gwp: 0, live: false });
    useEffect(() => {
        const previous = gwpBasisRef.current;
        const sameBasis = previous.live === isNonBinder;
        if (gwpAmount > 0) gwpBasisRef.current = { gwp: gwpAmount, live: isNonBinder };
        else gwpBasisRef.current = { gwp: previous.gwp, live: isNonBinder };
        if (!editing || !isNonBinder || !sameBasis || !previous.gwp || !gwpAmount || previous.gwp === gwpAmount) return;
        const rescale = (amount: string) => {
            const value = Number(amount) || 0;
            return value ? String(Math.round((value / previous.gwp) * gwpAmount)) : amount;
        };
        setDiscountAmount(rescale);
        setCashbackAmount(rescale);
        setCommissionValue(rescale);
        setPointsValue(rescale);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gwpAmount, isNonBinder, editing]);
    // CRM's giveaway budget is shared between Discount and Commission (Discount % + Commission %
    // reaching the cap unlocks Cashback), but Agency's Commission is derived FROM the cap minus
    // Discount (Commission % = cap - Discount %), so it's always already "used up" by construction -
    // summing it in here would mean Agency's own Discount alone reaching e.g. 15% (with the
    // remaining 10% automatically landing in Commission) wrongly unlocks Cashback early. Agency
    // needs Discount % alone at the cap.
    const cashbackUnlocked = isCrmLead
        ? (displayDiscountPer + agentCommissionPer) >= (cashbackUnlockPer - 0.01)
        : displayDiscountPer >= (cashbackUnlockPer - 0.01);
    // Same tax-deduction AdditionalDiscountPopup.tsx (Agency App/Web) applies — mirrors
    // deriveEffectiveCashbackAmount on the backend, recomputed live instead of showing the stale
    // snapshot value as Cashback Amount is edited.
    const cashbackTaxPercent = +current?.policy_holder === POLICY_HOLDER_PERSONAL ? PERSONAL_OFFSET_POINTS_TAX_PERCENT
        : +current?.policy_holder === POLICY_HOLDER_COMPANY ? COMPANY_OFFSET_POINTS_TAX_PERCENT : 0;
    const effectiveCashbackAmount = Math.round((Number(cashbackAmount) || 0) - (Number(cashbackAmount) || 0) * cashbackTaxPercent / 100);
    // Subtracts the tax-adjusted Effective Cashback, not the raw Cashback Amount — what the customer
    // actually nets off the premium is the post-tax figure.
    const finalPremiumAmount = Math.round(gwpAmount + (Number(adminFee) || 0) - (isCrmLead ? (Number(cashbackAmount) || 0) : effectiveCashbackAmount) - (Number(discountAmount) || 0));

    const insurancePeriodText = current?.insurance_starting_date
        ? `${moment(current.insurance_starting_date).format('DD MMM YYYY')} – ${moment(current.insurance_starting_date).add(tenure, 'years').format('DD MMM YYYY')}`
        : '';

    // Agency only — Commission stays read-only there, auto-filled off Discount to hit the 25% cap
    // (global.CUSTOMER_DISCOUNT_MAX), replacing the old Rule Engine round-trip for this pair.
    // CRM types every field by hand (Discount, Cashback, Commission, Points) with no auto-linking
    // between them — only the overall giveaway cap still gets enforced at Save/validate.
    const handleDiscountAmountChange = (newDiscountAmount: string) => {
        setDiscountAmount(newDiscountAmount);
        if (isCrmLead) return;
        const newDiscountPer = gwpAmount ? ((Number(newDiscountAmount) || 0) / gwpAmount) * 100 : 0;
        const newCommissionPer = Math.max(0, maxDiscountPer - newDiscountPer);
        setCommissionValue(String(Math.round(gwpAmount * newCommissionPer / 100)));
    };

    // Discount % is just another view of Discount Amount (same relationship as Cashback Amount/% below) —
    // convert straight to the amount and run it through the same path as the Amount field.
    const handleDiscountPerChange = (raw: string) => {
        const newPer = Number(raw.replace(/[^0-9.]/g, '')) || 0;
        handleDiscountAmountChange(String(gwpAmount ? Math.round(gwpAmount * newPer / 100) : 0));
    };

    // CRM only — Commission %/Points % are just another view of their own Value fields (same
    // relationship as Discount Amount/% above), so ops can type either side.
    const handleCommissionPerChange = (raw: string) => {
        const newPer = Number(raw.replace(/[^0-9.]/g, '')) || 0;
        setCommissionValue(String(gwpAmount ? Math.round(gwpAmount * newPer / 100) : 0));
    };

    const handlePointsPerChange = (raw: string) => {
        const newPer = Number(raw.replace(/[^0-9.]/g, '')) || 0;
        setPointsValue(String(gwpAmount ? Math.round(gwpAmount * newPer / 100) : 0));
    };

    // Cashback Amount / % / Effective Amount are three views of the same underlying cashbackAmount —
    // editing any one converts straight to the gross amount (the real stored value) and the other two
    // re-derive from it on the next render, same three-way linkage AdditionalDiscountPopup.tsx
    // (Agency App/Web) offers via its own handleOffsetPoints*Change handlers.
    const handleCashbackPerChange = (raw: string) => {
        const newPer = Number(raw.replace(/[^0-9.]/g, '')) || 0;
        setCashbackAmount(String(gwpAmount ? Math.round(gwpAmount * newPer / 100) : 0));
    };

    const handleEffectiveCashbackAmountChange = (raw: string) => {
        const newEffectiveAmount = Number(parseAmountInput(raw)) || 0;
        const divisor = 1 - cashbackTaxPercent / 100;
        setCashbackAmount(String(divisor > 0 ? Math.round(newEffectiveAmount / divisor) : newEffectiveAmount));
    };

    // Binder's numbers are the Rule Engine's, not ops's to keep — switching back to it after editing
    // under Non-Binder should discard those edits and restore the original baseline, not leave
    // whatever was last typed sitting there mislabeled as Binder's own figures. The true baseline is
    // current.original_quote (the quotation engine's own figures) — current itself is just whatever
    // was LAST SAVED, which for a lead previously saved under Non-Binder is that manual data, not
    // anything the Rule Engine computed.
    const handleRuleEngineChange = (newRuleEngineId: number) => {
        setRuleEngineId(newRuleEngineId);
        if (newRuleEngineId === WITH_RULE_ENGINE_ID && current) {
            const baseline = current.original_quote || current;
            // original_quote has no tenure field of its own — its years array length is the
            // real signal, since that's exactly how many years the quotation engine priced.
            const baselineTenure = baseline.tenure || baseline.years?.length || current.tenure || 1;
            setTenure(baselineTenure);
            // the original quote only knows the combined car price, so keep the TSI / accessories split the lead already has
            const baselineYears = (baseline.years || []).map((year: any, index: number) => ({
                ...year,
                estimated_tsi: year.estimated_tsi ?? current.years?.[index]?.estimated_tsi ?? null,
                accessories_price: year.accessories_price ?? current.years?.[index]?.accessories_price ?? null,
            }));
            setYears(toYearsFormState(baselineYears, baselineTenure, current.addon_catalog || []));
            setActiveYearTab(0);
        }
    };

    const handleTenureChange = (value: number) => {
        setTenure(value);
        setYears((prev) => {
            let next = [...prev];
            let year1Selected = (prev?.[0]?.addons || []).filter((addon: any) => addon.selected);
            while (next.length < value) next.push(emptyYear(prev?.[0]?.insurance_type_id, year1Selected));
            return next.slice(0, value);
        });
        setActiveYearTab(0);
    };

    const updateYearField = (index: number, field: string, value: any) => {
        setYears((prev) => prev.map((year, i) => (i === index ? { ...year, [field]: value } : year)));
    };

    const toggleAddon = (yearIndex: number, addonIndex: number) => {
        setYears((prev) => {
            let toggledAddons = [...prev[yearIndex].addons];
            toggledAddons[addonIndex] = { ...toggledAddons[addonIndex], selected: !toggledAddons[addonIndex].selected };
            let next = prev.map((year, i) => (i === yearIndex ? { ...year, addons: toggledAddons } : year));

            // Year 1's selection is every other year's addon list — re-sync them the moment it changes.
            if (yearIndex === 0) {
                let year1Selected = toggledAddons.filter((addon: any) => addon.selected);
                next = next.map((year, i) => (i === 0 ? year : { ...year, addons: mergeYearAddonsWithMaster(year1Selected, year.addons) }));
            }

            return next;
        });
    };

    const updateAddonField = (yearIndex: number, addonIndex: number, field: string, value: any) => {
        setYears((prev) => prev.map((year, i) => {
            if (i !== yearIndex) return year;
            let addons = [...year.addons];
            addons[addonIndex] = { ...addons[addonIndex], [field]: value };
            return { ...year, addons };
        }));
    };

    const validate = (): string[] => {
        let list: string[] = [];
        if (!plateNumber?.trim()) list.push(translations.Insurance?.['Plate Number is required'] || 'Plate Number is required');

        if (displayDiscountPer + agentCommissionPer > maxDiscountPer + 0.01) {
            // Room left for Discount alone given the current Commission %, so ops knows exactly how
            // much to bring the Discount Amount/% down by instead of just seeing it's over.
            const maxAllowedDiscountPer = Math.max(0, +(maxDiscountPer - agentCommissionPer).toFixed(2));
            const maxAllowedDiscountAmount = Math.round(gwpAmount * maxAllowedDiscountPer / 100);
            list.push(`${t('Discount % + Commission %', 'Discount % + Commission %')} (${(displayDiscountPer + agentCommissionPer).toFixed(2)}%) ${t('can not exceed', 'can not exceed')} ${maxDiscountPer}% (${t('max Discount Amount', 'max Discount Amount')}: ${fmt(maxAllowedDiscountAmount)} / ${maxAllowedDiscountPer}%)`);
        } else if ((Number(cashbackAmount) > 0 || (isCrmLead && agentPointsValue > 0)) && (displayDiscountPer + agentCommissionPer) < (maxDiscountPer - 0.01)) {
            // Mirrors validateDiscountCashback's own check on the backend (DiscountCashbackValidation.ts).
            // The UI disables Cashback/Points once Discount % + Commission % drops back below the cap, but
            // a value typed in while unlocked can still be sitting in state if Discount/Commission changed
            // after — catch it here instead of only surfacing after a rejected Save.
            // Points is CRM-only in this check: for Agency it's always the Rule Engine's live-computed
            // figure, not something ops chose to enter, so a non-zero value there doesn't mean anything
            // was "activated" — it's just whatever's left over from the engine's own commission/points split.
            const currentPer = +(displayDiscountPer + agentCommissionPer).toFixed(2);
            const neededPer = +(maxDiscountPer - currentPer).toFixed(2);
            const neededAmount = Math.round(gwpAmount * neededPer / 100);
            list.push(`${t('Cashback only activates once Discount % + Commission % already reach', 'Cashback only activates once Discount % + Commission % already reach')} ${maxDiscountPer}% (${t('currently', 'currently')} ${currentPer}%, ${t('need', 'need')} ${neededPer}% / ${fmt(neededAmount)} ${t('more', 'more')})`);
        } else {
            const giveawayCapPer = current?.giveaway_cap_per;
            const giveawayCapLabel = current?.giveaway_cap_label || 'giveaway cap';
            const totalGiveawayPer = displayDiscountPer + cashbackPer + agentCommissionPer + agentPointsPer;
            if (giveawayCapPer && totalGiveawayPer > giveawayCapPer + 0.01) {
                list.push(`${t('Discount % + Cashback % + Commission % + Points %', 'Discount % + Cashback % + Commission % + Points %')} (${totalGiveawayPer.toFixed(2)}%) ${t('can not exceed', 'can not exceed')} ${giveawayCapLabel} ${t('for this deal', 'for this deal')} (${giveawayCapPer}%)`);
            }
        }

        // Agency only — mirrors AdditionalDiscountPopup.tsx's own cap: Cashback beyond the available
        // Agent Points ceiling drives the Rule Engine's point_commision negative server-side.
        if (!isCrmLead && maxAgentPointsPer !== null && cashbackPer > maxAgentPointsPer + 0.01) {
            list.push(`${t('Customer Cashback %', 'Customer Cashback %')} (${cashbackPer}%) ${t('cannot be more than the available Agent Points', 'cannot be more than the available Agent Points')} (${maxAgentPointsPer}%)`);
        }

        if (isNonBinder) {
            years.forEach((year, index) => {
                if (!Number(year.estimated_tsi)) list.push(`${translations.Insurance?.['Estimated TSI is mandatory for year'] || 'Estimated TSI is mandatory for year'} ${index + 1}`);
                if (!Number(year.basic_premium_amount)) list.push(`${translations.Insurance?.['Basic Premium is mandatory for year'] || 'Basic Premium is mandatory for year'} ${index + 1}`);
                // A premium greater than the insured value it's covering can't be right — same
                // sanity cap applies to Basic Premium. Add-ons are capped as a group (their combined
                // total against TSI), not one by one — several modest add-ons can legitimately add up
                // close to TSI without any single one being unreasonable on its own.
                if (Number(year.estimated_tsi) && Number(year.basic_premium_amount) > Number(year.estimated_tsi)) {
                    list.push(`${translations.Insurance?.['Basic Premium cannot be more than Estimated TSI for year'] || 'Basic Premium cannot be more than Estimated TSI for year'} ${index + 1}`);
                }
                const selectedAddons = (year.addons || []).filter((a: any) => a.selected);
                selectedAddons.forEach((addon: any) => {
                    if (!Number(addon.premium_amount)) list.push(`${translations.Insurance?.['Premium is mandatory for'] || 'Premium is mandatory for'} ${addon.name} (${translations.commonMsg?.['Year'] || 'Year'} ${index + 1})`);
                });
                const totalSelectedAddonPremium = selectedAddons.reduce((sum: number, addon: any) => sum + (Number(addon.premium_amount) || 0), 0);
                if (Number(year.estimated_tsi) && totalSelectedAddonPremium > Number(year.estimated_tsi)) {
                    list.push(`${translations.Insurance?.['Total Add-on Premium cannot be more than Estimated TSI for year'] || 'Total Add-on Premium cannot be more than Estimated TSI for year'} ${index + 1}`);
                }
            });
        }
        return list;
    };

    const buildPayload = () => ({
        lead_id: queryParams.id,
        plate_number: plateNumber,
        customer_discount_amount: Number(discountAmount) || 0,
        customer_cashback_amount: Number(cashbackAmount) || 0,
        agent_commission_value: agentCommissionValue,
        agent_points_value: agentPointsValue,
        admin_fee: Number(adminFee) || 0,
        rule_engine_id: ruleEngineId,
        tenure,
        ...(isNonBinder ? {
            years: years.map((year) => ({
                estimated_tsi: Number(year.estimated_tsi) || 0,
                accessories_price: Number(year.accessories_price) || 0,
                basic_premium_amount: Number(year.basic_premium_amount) || 0,
                addons: (year.addons || []).filter((a: any) => a.selected).map((a: any) => ({
                    addon_id: a.addon_id,
                    premium_amount: Number(a.premium_amount) || 0,
                    coverage_amount: Number(a.coverage_amount) || 0,
                    seat_capacity: Number(a.seat_capacity) || 0,
                })),
            })),
        } : {}),
    });

    const afterSaveSuccess = (res: any) => {
        toast.success(res.message);
        let policyTermsSubStatus = STATUS_SUB_STATUS['subStatus']['policy_terms_finalized'];
        let prevIds: string = insuranceState.leadDetail.prev_status_sub_status_ids || '';
        let pair = `${STATUS_SUB_STATUS['status']['insurance_policy']}-${policyTermsSubStatus}`;
        // This Tab.Pane never unmounts, and other pages (e.g. Quote Details) read these same fields
        // straight off the shared leadDetail store, not a fresh fetch — without this, a save here only
        // ever updates this form's own local state, leaving every other page showing pre-save values.
        const snapshot = res.data?.current;
        dispatch(updateLeadDetailsInStore({
            ...insuranceState.leadDetail,
            prev_status_sub_status_ids: prevIds.includes(pair) ? prevIds : (prevIds ? `${prevIds},${pair}` : pair),
            ...(snapshot ? {
                customer_discount: snapshot.customer_discount_amount,
                customer_discount_per: snapshot.customer_discount_per,
                offset_points_amt: snapshot.customer_cashback_amount,
                offset_points: snapshot.customer_cashback_per,
                offset_points_amt_after_tax: snapshot.effective_customer_cashback_amount,
                admin_fee: snapshot.admin_fee,
                gwp_amount: snapshot.gwp_amount,
                final_premium: snapshot.final_premium_amount,
                agent_commision: snapshot.agent_commission_per,
                agent_comission_amount: snapshot.agent_commission_value,
                point_commision: snapshot.agent_points_per,
                agent_point_amount: snapshot.agent_points_value,
                plate_no: snapshot.plate_number,
                rule_engine_id: snapshot.rule_engine_id,
                tenure: snapshot.tenure,
            } : {}),
        }));
        navigate(`/details/insurance-policy-shared/${queryParams.id}`);
    };

    const handleConfirmNoChange = () => {
        setSaving(true);
        confirmPolicyTermsNoChange({ lead_id: queryParams.id }).then((res: any) => {
            setSaving(false);
            if (res?.status === STATUS_SUCCESS && res?.data) {
                afterSaveSuccess(res);
            } else {
                toast.error(res?.message || 'Failed to Update');
            }
        }).catch(() => {
            setSaving(false);
            toast.error('An error occurred');
        });
    };

    const proceedWithSave = () => {
        setSaving(true);
        savePolicyTermsFinalized(buildPayload()).then((res: any) => {
            setSaving(false);
            if (res?.status === STATUS_SUCCESS && res?.data) {
                afterSaveSuccess(res);
            } else {
                toast.error(res?.message || 'Failed to Update');
            }
        }).catch(() => {
            setSaving(false);
            toast.error('An error occurred');
        });
    };

    // What's actually about to change, field by field against the snapshot this edit started
    // from — shown in the save-confirm modal so ops reviews the real diff, not just a generic
    // "are you sure". Per-year Basic Premium is included (the figure ops is most often correcting);
    // add-on line items are left out to keep this a quick review, not a full audit trail (that's
    // what View History is for).
    const buildChangeSummary = () => {
        if (!current) return [];
        const changes: { label: string; oldValue: string; newValue: string }[] = [];
        const addIfChanged = (label: string, oldValue: any, newValue: any, format: (v: any) => string = (v) => String(v)) => {
            if (String(oldValue ?? '') !== String(newValue ?? '')) {
                changes.push({ label, oldValue: format(oldValue), newValue: format(newValue) });
            }
        };
        addIfChanged(t('Plate Number', 'Plate Number'), current.plate_number, plateNumber);
        addIfChanged(t('Customer Discount Amount', 'Customer Discount Amount'), current.customer_discount_amount, Number(discountAmount) || 0, fmt);
        addIfChanged(t('Customer Cashback Amount', 'Customer Cashback Amount'), current.customer_cashback_amount, Number(cashbackAmount) || 0, fmt);
        addIfChanged(t('Agent Commission Value', 'Agent Commission Value'), current.agent_commission_value, agentCommissionValue, fmt);
        addIfChanged(t('Agent Points Value', 'Agent Points Value'), current.agent_points_value, agentPointsValue, fmt);
        addIfChanged(t('Admin Fee', 'Admin Fee'), current.admin_fee, Number(adminFee) || 0, fmt);
        addIfChanged(t('Application Type', 'Application Type'), current.rule_engine_id, ruleEngineId, (v) => +v === WITH_RULE_ENGINE_ID ? t('Binder', 'Binder') : t('Non-Binder', 'Non-Binder'));
        addIfChanged(t('Insurance Tenure', 'Insurance Tenure'), current.tenure, tenure, (v) => `${v} Yr`);
        if (isNonBinder) {
            years.forEach((year, index) => {
                const origYear = current.years?.[index];
                addIfChanged(`${t('Year', 'Year')} ${index + 1} ${t('Basic Premium', 'Basic Premium')}`, origYear?.basic_premium_amount, Number(year.basic_premium_amount) || 0, fmt);
            });
        }
        return changes;
    };

    const handleSaveEditedTerms = () => {
        if (validate().length) {
            return;
        }
        // Always confirm before committing — an in-page modal, not window.confirm, since a native
        // browser dialog is invisible/unreliable inside embedded or automated browser contexts
        // (e.g. this app's own preview tooling). Skip it only when there's truly nothing to review.
        const changes = buildChangeSummary();
        const isSwitchingToNonBinder = +current?.rule_engine_id === WITH_RULE_ENGINE_ID && isNonBinder;
        if (changes.length || isSwitchingToNonBinder) {
            setSaveChangeSummary(changes);
            setShowNonBinderConfirm(true);
            return;
        }
        proceedWithSave();
    };

    // addCurrencyFormatting locale-formats with CURRENCY_CODE (id-ID: "." thousand separator), unlike
    // a bare toLocaleString() which falls back to the runtime's locale (commas).
    const fmt = (value: any) => HELPER.addCurrencyFormatting(value);

    const t = (key: string, fallback: string) => translations.Insurance?.[key] || fallback;

    const addonModalYear = addonModalYearIndex !== null ? years[addonModalYearIndex] : null;
    const addonModalTotals = addonModalYearIndex !== null ? yearTotals[addonModalYearIndex] : null;
    const addonModalIndexedAddons = (addonModalYear?.addons || []).map((addon: any, addonIndex: number) => ({ addon, addonIndex }));
    const addonModalBasicEntries = addonModalIndexedAddons.filter(({ addon }: any) => +addon.addon_id < SPECIAL_ADDON_START_ID);
    const addonModalSpecialEntries = addonModalIndexedAddons.filter(({ addon }: any) => +addon.addon_id >= SPECIAL_ADDON_START_ID);

    // recomputed on every render (pure, cheap) so the error list and the Save button's disabled
    // state stay live as the ops user types, instead of only surfacing on a failed Save attempt.
    const formErrors = editing ? validate() : [];

    // oldValue is the same field, formatted the same way, from the original final quote baseline —
    // when it differs from the current value, show it struck through with the new value beside it
    // (matches what changed since the deal was first quoted), otherwise just the plain value.
    // "Rp 2.518.432,5" -> 2518432.5; null for non-money text such as percentages
    const moneyValue = (text: any) => (typeof text === 'string' && text.startsWith('Rp') ? Number(text.replace(/[^0-9,]/g, '').replace(',', '.')) : null);
    // the original quote and the recomputed terms round at different steps, so a 1 Rupiah gap is not a change
    const isRoundingGap = (a: any, b: any) => {
        const x = moneyValue(a), y = moneyValue(b);
        return x !== null && y !== null && Math.abs(x - y) <= 1;
    };
    const summaryRow = (label: string, value: any, oldValue?: any) => {
        const changed = oldValue !== undefined && oldValue !== null && String(oldValue) !== String(value) && !isRoundingGap(oldValue, value);
        return (
            <div className="policy-terms-summary-row">
                <span className="lbl">{label}</span>
                {changed ? (
                    <span className="val policy-terms-summary-changed">
                        <span className="old-value">{oldValue}</span>
                        <i className="ic-keyboard_arrow_right"></i>
                        <span className="new-value">{value}</span>
                    </span>
                ) : (
                    <span className="val">{value}</span>
                )}
            </div>
        );
    };

    // Read-only breakup of the currently-saved terms — same section grouping as the Edit Terms
    // form and the same "breakup" pattern Quote Details shows, so ops can see where things stand
    // without opening Edit Terms. Diffs against the original final quote (current.original_quote)
    // wherever that baseline exists, so a changed field is visible at a glance.
    const renderTermsBreakup = () => {
        if (!current) return null;
        const isCurrentNonBinder = +current.rule_engine_id === WITHOUT_RULE_ENGINE;
        const orig = current.original_quote;

        return (
            <div className="policy-terms-breakup">
                <div className="policy-terms-section-title">{t('Discount, Cashback, Commission & Points', 'Discount, Cashback, Commission & Points')}</div>
                {summaryRow(t('Customer Discount Amount', 'Customer Discount Amount'), fmt(current.customer_discount_amount), orig && fmt(orig.customer_discount_amount))}
                {summaryRow(t('Customer Discount %', 'Customer Discount %'), `${current.customer_discount_per || 0}%`, orig && `${orig.customer_discount_per || 0}%`)}
                {summaryRow(t('Customer Cashback Amount', 'Customer Cashback Amount'), fmt(current.customer_cashback_amount), orig && fmt(orig.customer_cashback_amount))}
                {summaryRow(t('Customer Cashback %', 'Customer Cashback %'), `${current.customer_cashback_per || 0}%`, orig && `${orig.customer_cashback_per || 0}%`)}
                {!isCrmLead && summaryRow(t('Effective Customer Cashback Amount', 'Effective Customer Cashback Amount'), fmt(current.effective_customer_cashback_amount), orig && fmt(orig.effective_customer_cashback_amount))}
                {summaryRow(t('Agent Commission %', 'Agent Commission %'), `${current.agent_commission_per || 0}%`, orig && `${orig.agent_commission_per || 0}%`)}
                {summaryRow(t('Agent Commission Value', 'Agent Commission Value'), fmt(current.agent_commission_value), orig && fmt(orig.agent_commission_value))}
                {summaryRow(t('Agent Points %', 'Agent Points %'), `${current.agent_points_per || 0}%`, orig && `${orig.agent_points_per || 0}%`)}
                {summaryRow(t('Agent Points Value', 'Agent Points Value'), fmt(current.agent_points_value), orig && fmt(orig.agent_points_value))}

                <div className="policy-terms-section-title">{t('Premium Determination', 'Premium Determination')}</div>
                {summaryRow(t('Application Type', 'Application Type'), isCurrentNonBinder ? t('Non-Binder', 'Non-Binder') : t('Binder', 'Binder'))}
                {summaryRow(t('Insurance Tenure', 'Insurance Tenure'), `${current.tenure || 1} Yr`)}
                {(current.years || []).map((year: any, index: number) => {
                    const addonPremium = (year.addons || []).reduce((sum: number, a: any) => sum + (Number(a.premium_amount) || 0), 0);
                    const origYear = orig?.years?.find((y: any) => y.year === year.year);
                    const origAddonPremium = origYear ? (origYear.addons || []).reduce((sum: number, a: any) => sum + (Number(a.premium_amount) || 0), 0) : undefined;
                    return (
                        <div key={index} className="policy-terms-breakup-year">
                            {(current.years || []).length > 1 && <div className="policy-terms-breakup-year-label">{translations.commonMsg?.['Year'] || 'Year'} {index + 1}</div>}
                            {summaryRow(t('Estimated TSI', 'Estimated TSI'), year.estimated_tsi != null ? fmt(year.estimated_tsi) : t('N/A', 'N/A'), (origYear?.estimated_tsi != null) && fmt(origYear.estimated_tsi))}
                            {summaryRow(t('Price of Accessories', 'Price of Accessories'), year.accessories_price != null ? fmt(year.accessories_price) : t('N/A', 'N/A'), (origYear?.accessories_price != null) && fmt(origYear.accessories_price))}
                            {summaryRow(t('Overall Car Price', 'Overall Car Price'), fmt(year.overall_car_price), origYear && fmt(origYear.overall_car_price))}
                            {summaryRow(t('Basic Premium', 'Basic Premium'), fmt(year.basic_premium_amount), origYear && fmt(origYear.basic_premium_amount))}
                            {summaryRow(t('Total Add-on Premium', 'Total Add-on Premium'), fmt(addonPremium), origAddonPremium !== undefined && fmt(origAddonPremium))}
                        </div>
                    );
                })}

                <div className="policy-terms-section-title">{t('Aggregate Totals', 'Aggregate Totals')}</div>
                {summaryRow(t('Gross Written Premium', 'Gross Written Premium'), fmt(current.gwp_amount), orig && fmt(orig.gwp_amount))}
                {summaryRow(t('Admin Fee', 'Admin Fee'), fmt(current.admin_fee), orig && fmt(orig.admin_fee))}
                {summaryRow(t('Final Premium Amount', 'Final Premium Amount'), fmt(current.final_premium_amount), orig && fmt(orig.final_premium_amount))}
            </div>
        );
    };

    return (
        <div className="image-form-outer">
            <FullPageLoader show={loading || saving} />
            <div className="image-tab-panel">
                <DocumentGallery formType="policy-terms-finalized" />
            </div>
            <div className="form-tab-right-panel wrapperPosition inspolicyshared">
                <div className="lead-detail-from-outer">
                    <div className="lead-form-heading">
                        <h2>{translations.Insurance?.['Policy Terms Finalized'] || translations.commonMsg?.loading}</h2>
                    </div>

                    {
                        !disbursalApproved ? (
                            <div className="lead-form-filed">
                                <div className="policy-terms-locked-banner">
                                    <h4>🔒 {t('Locked — Disbursal document not yet approved', 'Locked — Disbursal document not yet approved')}</h4>
                                    <p>{t('Switch to the Docs Upload tab above and approve the document before finalizing terms here.', 'Switch to the Docs Upload tab above and approve the document before finalizing terms here.')}</p>
                                    <button type="button" className="btn-primary" onClick={() => props.setTopLevelTab?.('third')}>{t('Go to Docs Upload', 'Go to Docs Upload')}</button>
                                </div>
                            </div>
                        ) : locked ? (
                            <div className="lead-form-filed">
                                {renderTermsBreakup()}
                            </div>
                        ) : !editing ? (
                            <div className="lead-form-filed">
                                {renderTermsBreakup()}
                                {
                                    canEdit ? (
                                        <div className="policy-terms-choice-box">
                                            <h4>{t('Have the negotiated terms changed from the original quotation?', 'Have the negotiated terms changed from the original quotation?')}</h4>
                                            <p>{t('Compare against the policy document on the left before choosing.', 'Compare against the policy document on the left before choosing.')}</p>
                                            <div className="btn-save-remarks">
                                                <button type="button" className="btn-primary" onClick={handleConfirmNoChange}>{translations.commonMsg?.['Confirm – No Changes'] || 'Confirm – No Changes'}</button>
                                                <button type="button" className="btn-line" onClick={() => setEditing(true)}>{translations.commonMsg?.['Edit Terms'] || 'Edit Terms'}</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button type="button" className="btn-primary" disabled>{translations.commonMsg?.['Confirm – No Changes'] || 'Confirm – No Changes'}</button>
                                    )
                                }
                            </div>
                        ) : (
                            <div className="lead-form-filed">
                                <div className="policy-terms-section-title">{t('Vehicle Details', 'Vehicle Details')}</div>
                                <div className="row">
                                    <fieldset className="col-md-12">
                                        <div className="material" title={plateNumber}>
                                            <input value={plateNumber} onChange={(e: any) => setPlateNumber(e.target.value)} type="text" placeholder=" " className="form-input" autoComplete="off" />
                                            <label data-label={`${t('Plate Number', 'Plate Number')} *`} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                </div>

                                <div className="policy-terms-section-title">{t('Discount, Cashback, Commission & Points', 'Discount, Cashback, Commission & Points')}</div>
                                <div className="row">
                                    <fieldset className="col-md-6">
                                        <div className="material" title={discountAmount}>
                                            <AmountInput value={discountAmount} onValueChange={handleDiscountAmountChange} />
                                            <label data-label={t('Customer Discount Amount', 'Customer Discount Amount')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                    <fieldset className="col-md-6">
                                        <div className="material">
                                            <PercentInput value={gwpAmount ? displayDiscountPer : ''} onValueChange={handleDiscountPerChange} />
                                            <label data-label={t('Customer Discount %', 'Customer Discount %')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                    <fieldset className="col-md-6">
                                        <div className="material" title={cashbackAmount}>
                                            <AmountInput value={cashbackAmount} onValueChange={setCashbackAmount} disabled={!cashbackUnlocked} />
                                            <label data-label={t('Customer Cashback Amount', 'Customer Cashback Amount')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                    <fieldset className="col-md-6">
                                        <div className="material">
                                            <PercentInput value={gwpAmount ? cashbackPer : ''} onValueChange={handleCashbackPerChange} disabled={!cashbackUnlocked} />
                                            <label data-label={t('Customer Cashback %', 'Customer Cashback %')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                    {!isCrmLead && maxAgentPointsPer !== null &&
                                    <fieldset className="col-md-12" style={{ marginBottom: 0 }}>
                                        <p className="policy-terms-callout" style={{ margin: '0 0 12px' }}>
                                            {t('Maximum Usable Points', 'Maximum Usable Points')}: {maxAgentPointsPer}% ({t('Effective Offset', 'Effective Offset')}: {+(maxAgentPointsPer - maxAgentPointsPer * cashbackTaxPercent / 100).toFixed(2)}% {t('after tax', 'after tax')})
                                        </p>
                                    </fieldset>}
                                    {!isCrmLead &&
                                    <fieldset className="col-md-12">
                                        <div className="material">
                                            <AmountInput value={String(effectiveCashbackAmount)} onValueChange={handleEffectiveCashbackAmountChange} disabled={!cashbackUnlocked} />
                                            <label data-label={t('Effective Customer Cashback Amount', 'Effective Customer Cashback Amount')} className="form-label"></label>
                                        </div>
                                    </fieldset>}
                                    <fieldset className="col-md-6">
                                        <div className="material">
                                            <AmountInput value={commissionValue} onValueChange={setCommissionValue} disabled={!isCrmLead} />
                                            <label data-label={t('Agent Commission Value', 'Agent Commission Value')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                    <fieldset className="col-md-6">
                                        <div className="material">
                                            <PercentInput value={gwpAmount ? agentCommissionPer : ''} onValueChange={handleCommissionPerChange} disabled={!isCrmLead} />
                                            <label data-label={t('Agent Commission %', 'Agent Commission %')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                    <fieldset className="col-md-6">
                                        <div className="material">
                                            <AmountInput value={pointsValue} onValueChange={setPointsValue} disabled={!isCrmLead || !cashbackUnlocked} />
                                            <label data-label={t('Agent Points Value', 'Agent Points Value')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                    <fieldset className="col-md-6">
                                        <div className="material">
                                            <PercentInput value={gwpAmount ? agentPointsPer : ''} onValueChange={handlePointsPerChange} disabled={!isCrmLead || !cashbackUnlocked} />
                                            <label data-label={t('Agent Points %', 'Agent Points %')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                </div>
                                {isCrmLead && !cashbackUnlocked &&
                                <p className="policy-terms-callout">
                                    {`${t('Cashback and Points activate once Discount % + Commission % reach', 'Cashback and Points activate once Discount % + Commission % reach')} ${cashbackUnlockPer}%`}
                                </p>}
                                {!isCrmLead &&
                                <p className="policy-terms-callout">
                                    {!cashbackUnlocked
                                        ? `${t('Cashback fields activate once Discount % reaches', 'Cashback fields activate once Discount % reaches')} ${cashbackUnlockPer}%`
                                        : `${t('Discount % capped at', 'Discount % capped at')} ${maxDiscountPer}%. ${t('Cashback activates only once', 'Cashback activates only once')} ${cashbackUnlockPer}% ${t('is reached.', 'is reached.')}`}
                                </p>}

                                <fieldset className="col-md-12 policy-terms-radio-row">
                                    <label className="form-label-static">{t('Application Type', 'Application Type')} *</label>
                                    <div className="custom-control custom-radio radio-inline">
                                        <input type="radio" className="custom-control-input" name="policy_terms_application_type"
                                            checked={+ruleEngineId === WITH_RULE_ENGINE_ID}
                                            disabled={+current?.rule_engine_id === WITHOUT_RULE_ENGINE}
                                            onChange={() => handleRuleEngineChange(WITH_RULE_ENGINE_ID)} />
                                        <label className="custom-control-label">{t('Binder', 'Binder')}</label>
                                    </div>
                                    <div className="custom-control custom-radio radio-inline">
                                        <input type="radio" className="custom-control-input" name="policy_terms_application_type"
                                            checked={+ruleEngineId === WITHOUT_RULE_ENGINE}
                                            onChange={() => handleRuleEngineChange(WITHOUT_RULE_ENGINE)} />
                                        <label className="custom-control-label">{t('Non-Binder', 'Non-Binder')}</label>
                                    </div>
                                </fieldset>

                                <div className="policy-terms-section-title">
                                    {t('Premium Determination', 'Premium Determination')}
                                    {isNonBinder && <span className="policy-terms-badge">{t('unlocked on Non-Binder', 'unlocked on Non-Binder')}</span>}
                                </div>
                                <fieldset className="col-md-12">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="material">
                                                <select className="form-input" value={tenure} disabled={!isNonBinder} onChange={(e: any) => handleTenureChange(+e.target.value)}>
                                                    {TENURE_OPTIONS.map((option) => <option key={option} value={option}>{option} Yr</option>)}
                                                </select>
                                                <label data-label={t('Insurance Tenure', 'Insurance Tenure')} className="form-label"></label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="material">
                                                <input value={insurancePeriodText} disabled type="text" placeholder=" " className="form-input" />
                                                <label data-label={t('Insurance Period', 'Insurance Period')} className="form-label"></label>
                                            </div>
                                        </div>
                                    </div>

                                    {
                                        years.length > 1 &&
                                        <div className="policy-terms-year-tabs">
                                            {years.map((_, index) => (
                                                <button type="button" key={index} className={`policy-terms-year-tab ${activeYearTab === index ? 'active' : ''}`} onClick={() => setActiveYearTab(index)}>
                                                    {translations.commonMsg?.['Year'] || 'Year'} {index + 1}
                                                </button>
                                            ))}
                                        </div>
                                    }

                                    {
                                        years[activeYearTab] &&
                                        <div className="policy-terms-year-block">
                                            <div className="row">
                                                <fieldset className="col-md-6">
                                                    <div className="material">
                                                        <AmountInput value={years[activeYearTab].estimated_tsi} onValueChange={(v) => updateYearField(activeYearTab, 'estimated_tsi', v)} disabled={!isNonBinder} />
                                                        <label data-label={`${t('Estimated TSI', 'Estimated TSI')} *`} className="form-label"></label>
                                                    </div>
                                                </fieldset>
                                                <fieldset className="col-md-6">
                                                    <div className="material">
                                                        <AmountInput value={years[activeYearTab].accessories_price} onValueChange={(v) => updateYearField(activeYearTab, 'accessories_price', v)} disabled={!isNonBinder} />
                                                        <label data-label={t('Price of Accessories', 'Price of Accessories')} className="form-label"></label>
                                                    </div>
                                                </fieldset>
                                                <fieldset className="col-md-6">
                                                    <div className="material">
                                                        <input value={fmt(yearTotals[activeYearTab]?.overallCarPrice)} disabled type="text" placeholder=" " className="form-input" />
                                                        <label data-label={t('Overall Car Price', 'Overall Car Price')} className="form-label"></label>
                                                    </div>
                                                </fieldset>
                                                <fieldset className="col-md-6">
                                                    <div className="material">
                                                        <AmountInput value={years[activeYearTab].basic_premium_amount} onValueChange={(v) => updateYearField(activeYearTab, 'basic_premium_amount', v)} disabled={!isNonBinder} />
                                                        <label data-label={`${t('Basic Premium', 'Basic Premium')} *`} className="form-label"></label>
                                                    </div>
                                                </fieldset>
                                            </div>
                                            <div className="addons addons-filed">
                                                <div className="addonwrapper">
                                                    <label className="selectedWrapper">{yearTotals[activeYearTab]?.addonPremium ? `${(years[activeYearTab].addons || []).filter((a: any) => a.selected).length} ${t('Add Ons Selected', 'Add Ons Selected')} · ${fmt(yearTotals[activeYearTab]?.addonPremium)}` : `0 ${t('Add Ons Selected', 'Add Ons Selected')}`}</label>
                                                    <button type="button" className="btn-line btn-addon" onClick={() => setAddonModalYearIndex(activeYearTab)}>
                                                        {isNonBinder ? t('Select Add Ons', 'Select Add Ons') : t('View Add Ons', 'View Add Ons')}<i className="ic-keyboard_arrow_right"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                </fieldset>

                                <div className="policy-terms-section-title">{t('Aggregate Totals', 'Aggregate Totals')}</div>
                                <div className="row">
                                    <fieldset className="col-md-6">
                                        <div className="material">
                                            <input value={fmt(gwpAmount)} disabled type="text" placeholder=" " className="form-input" />
                                            <label data-label={t('Gross Written Premium', 'Gross Written Premium')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                    <fieldset className="col-md-6">
                                        <div className="material">
                                            <AmountInput value={adminFee} onValueChange={setAdminFee} />
                                            <label data-label={t('Admin Fee', 'Admin Fee')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                    <fieldset className="col-md-12">
                                        <div className="material policy-terms-final-premium">
                                            <input value={fmt(finalPremiumAmount)} disabled type="text" placeholder=" " className="form-input" />
                                            <label data-label={t('Final Premium Amount', 'Final Premium Amount')} className="form-label"></label>
                                        </div>
                                    </fieldset>
                                </div>

                                {
                                    !!formErrors.length &&
                                    <div className="policy-terms-error-list m-xl-t">
                                        {formErrors.map((err, index) => <div key={index}>{err}</div>)}
                                    </div>
                                }

                                <div className="btn-save-remarks m-xl-t">
                                    <button type="button" className="btn-primary" disabled={!!formErrors.length} onClick={handleSaveEditedTerms}>{translations.commonMsg?.['save'] || 'Save & Next'}</button>
                                    <button type="button" className="btn-line" onClick={() => { setEditing(false); resetFormFromCurrent(current); }}>{translations.commonMsg?.['Cancel'] || 'Cancel'}</button>
                                </div>
                            </div>
                        )
                    }
                </div>
            </div>

            <Modal show={addonModalYearIndex !== null} handleClose={() => setAddonModalYearIndex(null)}>
                <div className="policy-terms-addon-modal">
                    <h2>{t('Add Ons Selection', 'Add Ons Selection')}</h2>
                    <div className="policy-terms-addon-modal-body">
                        {
                            [
                                { label: t('Basic', 'Basic'), entries: addonModalBasicEntries },
                                { label: t('Special', 'Special'), entries: addonModalSpecialEntries },
                            ].map(({ label, entries }) => entries.length > 0 && (
                                <div key={label} className="policy-terms-addon-group">
                                    <h3 className="policy-terms-addon-group-title">{label}</h3>
                                    {
                                        entries.map(({ addon, addonIndex }: any) => (
                                            <div key={addon.addon_id} className="policy-terms-addon-card">
                                                <label className="custom-control custom-checkbox">
                                                    <input type="checkbox" className="custom-control-input" checked={!!addon.selected} disabled={!isNonBinder}
                                                        onChange={() => addonModalYearIndex !== null && toggleAddon(addonModalYearIndex, addonIndex)} />
                                                    <span className="custom-control-label">{addon.name}</span>
                                                </label>
                                                {
                                                    addon.selected && !!addon.coverage_list?.length &&
                                                    <ReactSelect
                                                        options={addon.coverage_list.map((opt: any) => ({ ...opt, label: HELPER.addCurrencyFormatting(opt.label) }))}
                                                        value={addon.coverage_amount ? { label: HELPER.addCurrencyFormatting(addon.coverage_amount), value: addon.coverage_amount } : null}
                                                        handleChange={(e: any) => addonModalYearIndex !== null && updateAddonField(addonModalYearIndex, addonIndex, 'coverage_amount', e.value)}
                                                        placeholder={t('Choose Coverage Amount', 'Choose Coverage Amount')}
                                                        isDisabled={!isNonBinder}
                                                        fieldKey=""
                                                    />
                                                }
                                                {
                                                    addon.selected && !!addon.seat_capacity_list?.length &&
                                                    <ReactSelect
                                                        options={addon.seat_capacity_list}
                                                        value={addon.seat_capacity ? { label: addon.seat_capacity, value: addon.seat_capacity } : null}
                                                        handleChange={(e: any) => addonModalYearIndex !== null && updateAddonField(addonModalYearIndex, addonIndex, 'seat_capacity', e.value)}
                                                        placeholder={t('Seating Capacity', 'Seating Capacity')}
                                                        isDisabled={!isNonBinder}
                                                        fieldKey=""
                                                    />
                                                }
                                                {
                                                    addon.selected &&
                                                    <div className="material">
                                                        <AmountInput value={addon.premium_amount} onValueChange={(v) => addonModalYearIndex !== null && updateAddonField(addonModalYearIndex, addonIndex, 'premium_amount', v)} disabled={!isNonBinder} />
                                                        <label data-label={`${t('Premium', 'Premium')} *`} className="form-label"></label>
                                                    </div>
                                                }
                                            </div>
                                        ))
                                    }
                                </div>
                            ))
                        }
                        {
                            !(addonModalYear?.addons || []).length &&
                            <p className="callout">{t('No add-ons on this quotation yet.', 'No add-ons on this quotation yet.')}</p>
                        }
                    </div>
                    <div className="policy-terms-addon-modal-foot">
                        <span>{(addonModalYear?.addons || []).filter((a: any) => a.selected).length} {t('Add Ons Selected', 'Add Ons Selected')} · {fmt(addonModalTotals?.addonPremium)}</span>
                        <button type="button" className="btn-primary" onClick={() => setAddonModalYearIndex(null)}>{isNonBinder ? t('Apply', 'Apply') : t('Close', 'Close')}</button>
                    </div>
                </div>
            </Modal>

            <Modal show={showNonBinderConfirm} handleClose={() => setShowNonBinderConfirm(false)}>
                <div className="policy-terms-confirm-modal">
                    <h2>{t('Confirm these changes?', 'Confirm these changes?')}</h2>
                    <p className="policy-terms-confirm-modal-message">{t('Please review the changes below before saving — this updates the policy record immediately.', 'Please review the changes below before saving — this updates the policy record immediately.')}</p>
                    {!!saveChangeSummary.length &&
                        <ul className="policy-terms-confirm-modal-changes">
                            {saveChangeSummary.map((change, index) => (
                                <li key={index}>
                                    <span className="lbl">{change.label}</span>
                                    <span className="val">{change.oldValue} → {change.newValue}</span>
                                </li>
                            ))}
                        </ul>}
                    {(+current?.rule_engine_id === WITH_RULE_ENGINE_ID && isNonBinder) &&
                        <p className="policy-terms-confirm-modal-warning">{t('Once saved, this lead can never be switched back to Binder — the Application Type will be locked to Non-Binder for good.', 'Once saved, this lead can never be switched back to Binder — the Application Type will be locked to Non-Binder for good.')}</p>}
                    <div className="policy-terms-confirm-modal-actions">
                        <button type="button" className="btn-line" onClick={() => setShowNonBinderConfirm(false)}>{translations.commonMsg?.['cancel'] || 'Cancel'}</button>
                        <button type="button" className="btn-primary" onClick={() => { setShowNonBinderConfirm(false); proceedWithSave(); }}>{t('Continue', 'Continue')}</button>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default PolicyTermsFinalizedForm;
